'use client';

import { Edit } from 'lucide-react';

import { useMemo } from 'react';

import Link from 'next/link';

import { Button } from '@/components/ui/button';

import { Tooltip } from '@/shared/components';
import { COPY } from '@/shared/constants/copies';

import { useCareersEdit } from '../../hooks/use-careers-edit';
import { CareersHeader } from '../common/careers-header';
import { EmptyState } from '../common/empty-state';
import { JobsList } from './jobs-list';

export function CareersPageContent() {
	const {
		careersData,
		isEditMode,
		pinnedForms,
		togglePin,
		maxPinnedForms,
		hasEditAccess,
		isAccessLoading,
		handle,
	} = useCareersEdit();

	const isAdmin = hasEditAccess && !isAccessLoading;

	// Sort forms: pinned first (in edit mode use edited pins, otherwise server pins)
	const sortedForms = useMemo(() => {
		const forms = careersData?.forms ?? [];
		const pinnedSet = new Set(pinnedForms);
		return [...forms].sort((a, b) => {
			const aPin = pinnedSet.has(a.identifier) ? 0 : 1;
			const bPin = pinnedSet.has(b.identifier) ? 0 : 1;
			return aPin - bPin;
		});
	}, [careersData?.forms, pinnedForms]);

	return (
		<div className="relative min-h-screen bg-[#F8F8F8]">
			{/* Top-right edit button (view mode only, for admins) */}
			{isAdmin && !isEditMode && (
				<div className="fixed right-6 top-6 z-10">
					<Tooltip text={COPY.access.manageAccess('page')}>
						<Link href={`/workspaces/${handle}/edit`}>
							<Button
								variant="light"
								size="sm"
							>
								<Edit className="h-3.5 w-3.5" />
								Edit
							</Button>
						</Link>
					</Tooltip>
				</div>
			)}

			<main className="container mx-auto max-w-2xl px-4 pb-16 pt-[80px]">
				{/* Header */}
				<CareersHeader />

				{/* Jobs list */}
				{sortedForms.length > 0 ? (
					<JobsList
						forms={sortedForms}
						isEditMode={isEditMode}
						pinnedForms={pinnedForms}
						onTogglePin={togglePin}
						maxPinnedForms={maxPinnedForms}
					/>
				) : (
					<EmptyState />
				)}
			</main>
		</div>
	);
}
